import { ReactNode } from "react";

import {
    CollateralWithTerms,
    Collateral,
    AbfLoanExpanded,
    StrategyExpandedInfo,
    MarketGuideMode,
    LoopExpanded,
    LendingVaultExpanded,
    ParsedVaultRewardsScheduleExpanded,
    ParsedMarketInformationWithLtvInfo,
    MarketAssetView,
    RefinanceOption,
    EarnVaultRecordView,
    LockboxDetail
} from "@bridgesplit/abf-react";
import { LoopRouteType, StrategyDuration, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { useDialog } from "@bridgesplit/ui";
import { Result } from "@bridgesplit/utils";
import { DispatchType } from "@bridgesplit/react";
import { TransactionsDialogData } from "app/components/transactions";
import { SlippageDialogProps } from "app/components/common/slippage";

import { MarketProps } from "../components/market-detail/types";
import { WindForm } from "../components/loop";
import { SocialShareDialogProps } from "../components/portfolio";
import { CollateralAmountsForm } from "../components/manager-view/type";

export enum AppDialog {
    TransactionSender = "Send transactions",
    MarketGuide = "MarketGuide",
    LoanActions = "LoanActions",
    BulkLoanActions = "BulkLoanActions",
    MergeLoans = "MergeLoans",
    BulkStrategyDeposit = "BulkStrategyDeposit",
    BulkStrategyWithdraw = "BulkStrategyWithdraw",
    RefinanceSettings = "RefinanceSettings",

    UpdateStrategyApyAndCollateral = "UpdateStrategyApyAndCollateral",

    // auth
    AddMember = "Add member",
    AddGroup = "Add organization",
    ModifyLoanRoles = "Modify roles",
    ModifyAdminRoles = "Modify permissions",
    DeleteMember = "Delete member",
    VerifyMember = "Verify member",
    CreateCustodianInvite = "Create invite",

    StrategyDelete = "Delete position",
    StrategyManageEscrow = "Deposit/Withdraw",
    StrategyTerms = "Update terms",
    StrategySettings = "Settings",

    MultiSelectCollateral = "MultiSelectCollateral",

    SelectToken = "Select token",
    BorrowSelectCollateral = "Select collateral",
    ReferralEnterCode = "Accept a referral",
    Lend = "Lend",
    Borrow = "Borrow",
    LendApy = "LendApy",
    WindSettings = "WindSettings", //
    WindSetTerm = "WindSetTerm",
    BorrowRiskConfirm = "BorrowRiskConfirm",
    UpdateTerms = "UpdateTerms",

    VaultManageEscrow = "VaultManageEscrow",
    VaultClaimRewards = "VaultClaimRewards",
    VaultWithdrawSlippage = "VaultWithdrawSlippage",

    TransactionSettings = "TransactionSettings",
    SlippageSettings = "SlippageSettings",

    ManagerAddRewards = "Add rewards",
    ManagerEditRewards = "Edit rewards",
    SocialShare = "SocialShare",
    Swap = "Swap",

    // Lockbox (gated by the lockbox feature flag)
    LockboxCreate = "LockboxCreate",
    LockboxDeposit = "LockboxDeposit",
    LockboxWithdraw = "LockboxWithdraw",
    LockboxLockUnlock = "LockboxLockUnlock",

    EarnDeposit = "EarnDeposit",
    EarnDepositNotice = "EarnDepositNotice",
    EarnWithdraw = "EarnWithdraw"
}

type DialogMap = {
    [AppDialog.TransactionSender]: TransactionsDialogData;
    [AppDialog.MultiSelectCollateral]: {
        header?: string;
        collateral: string[];
        tokens: TokenListMetadata[] | undefined;
        customMarketInformation?: string;
        setCollateral?: (collateral: CollateralWithTerms[], selectedMarketInfoAddress?: string) => void;
        setCollateralAsync?: (collateral: CollateralWithTerms[]) => Promise<Result<unknown>>;
        existingStrategy?: StrategyExpandedInfo;
        principalMint?: string;
        isAdvanced: boolean;
        collateralToMarketInfoOptions?: Map<string, MarketAssetView[]>;
        existingCollateralSelections?: Record<string, CollateralAmountsForm>;
        existingStrategyCollaterals?: Map<string, { oracleAddress: string; oracleType: number }>;
        enableMultiOracleSelection?: boolean;
        verifiedMarketInfos?: ParsedMarketInformationWithLtvInfo[];
        selectedMarketInfoAddress?: string; // For persistence
    };
    [AppDialog.SelectToken]: {
        tokens: TokenListMetadata[] | undefined;
        setToken: (token: TokenListMetadata) => void;
        header?: string;
        showBalances?: boolean;
    };

    [AppDialog.BorrowSelectCollateral]: {
        marketProps: MarketProps;
        strategyDuration: StrategyDuration;
        setToken: (token: Collateral) => void;
    };

    [AppDialog.AddMember]: void;
    [AppDialog.AddGroup]: void;

    [AppDialog.StrategyDelete]: { strategy: StrategyExpandedInfo };
    [AppDialog.StrategyManageEscrow]: { strategy: StrategyExpandedInfo; side: "deposit" | "withdraw" };
    [AppDialog.StrategyTerms]: { strategy: StrategyExpandedInfo };
    [AppDialog.StrategySettings]: { strategy: StrategyExpandedInfo };
    [AppDialog.LoanActions]: {
        loan: AbfLoanExpanded;
        action: { component: ReactNode; dialogHeaderLabel?: string };
        isDrawer?: boolean;
        maxWidth?: number;
    };
    [AppDialog.BulkLoanActions]: { loans: AbfLoanExpanded[] };
    [AppDialog.MergeLoans]: { loanAddresses: string[] };
    [AppDialog.BulkStrategyDeposit]: { strategyAddresses: string[] };
    [AppDialog.BulkStrategyWithdraw]: { strategyAddresses: string[] };
    [AppDialog.RefinanceSettings]: { loan: AbfLoanExpanded };
    [AppDialog.Lend]: { token: TokenListMetadata };
    [AppDialog.UpdateStrategyApyAndCollateral]: {
        token: TokenListMetadata;
        strategy: StrategyExpandedInfo;
    };
    [AppDialog.Borrow]: { token: TokenListMetadata };

    [AppDialog.MarketGuide]: {
        mode: MarketGuideMode;
        isForced?: boolean;
        isNestedDialog?: boolean;
        loopExpanded?: LoopExpanded; //Added for loops guide
        vaultExpanded?: LendingVaultExpanded;
    };

    [AppDialog.LendApy]: {
        setApy: (apy: number | undefined) => void;
        marketMinApy: number;
        defaultRate: number;
        currentApy: number;
    };
    [AppDialog.WindSettings]: { loopExpanded: LoopExpanded; form: WindForm; setForm: DispatchType<WindForm> };
    [AppDialog.WindSetTerm]: {
        strategyDurations: StrategyDuration[] | undefined;
        strategyDuration: StrategyDuration | undefined;
        setStrategyDuration: (strategyDuration: StrategyDuration) => void;
    };
    [AppDialog.BorrowRiskConfirm]: {
        netApyPct: number | undefined;
        borrowApy: number | undefined;
        avgApy: number | undefined;
        negativeNetApy: boolean;
        borrowApyAboveAvg: boolean;
        onConfirm: () => Promise<Result<unknown>>;
    };

    [AppDialog.ReferralEnterCode]: { initialCode?: string };

    [AppDialog.VaultManageEscrow]: { vaultExpanded: LendingVaultExpanded; side: "deposit" | "withdraw" };
    [AppDialog.VaultClaimRewards]: { vaultExpanded: LendingVaultExpanded };
    [AppDialog.TransactionSettings]: void;
    [AppDialog.SlippageSettings]: SlippageDialogProps;
    [AppDialog.VaultWithdrawSlippage]: { slippageSetting: number; setSlippageSetting: DispatchType<number> };

    [AppDialog.ManagerAddRewards]: { vaultExpanded: LendingVaultExpanded };
    [AppDialog.ManagerEditRewards]: {
        schedule: ParsedVaultRewardsScheduleExpanded;
        principalMint: string;
        vaultExpanded: LendingVaultExpanded;
    };
    [AppDialog.SocialShare]: SocialShareDialogProps;
    [AppDialog.Swap]: {
        buyToken: TokenListMetadata | undefined;
        sellToken: TokenListMetadata | undefined;
        routeType?: LoopRouteType | null;
    };
    [AppDialog.UpdateTerms]: {
        option: RefinanceOption | undefined;
        validOptions: RefinanceOption[] | undefined;
        optionsWithOffsets: RefinanceOption[] | undefined;
        optionsWithQuotes: RefinanceOption[] | undefined;
        setOption: (option: RefinanceOption | undefined) => void;
    };

    [AppDialog.LockboxCreate]: void;
    [AppDialog.LockboxDeposit]: { lockbox: LockboxDetail };
    [AppDialog.LockboxWithdraw]: { lockbox: LockboxDetail; assetMint: string };
    [AppDialog.LockboxLockUnlock]: { lockbox: LockboxDetail };

    [AppDialog.EarnDeposit]: { vault: EarnVaultRecordView };
    [AppDialog.EarnDepositNotice]: { vault: EarnVaultRecordView };
    [AppDialog.EarnWithdraw]: { vault: EarnVaultRecordView; amount?: number; payoutMint?: string };
};

export type AppDialogData<T extends AppDialog> = T extends keyof DialogMap ? DialogMap[T] : void;

export type AppDialogOpen = <T extends AppDialog>(type: T, data: AppDialogData<T>, addToStack?: boolean) => void;

export function useAppDialog() {
    const dialog = useDialog();

    function open<T extends AppDialog>(type: T, data: AppDialogData<T>, addToStack = false) {
        if (addToStack) {
            dialog.addToStack<AppDialogData<T>>(type, data);
        } else {
            dialog.open<AppDialogData<T>>(type, data);
        }
    }

    function getData<T extends AppDialog>(identifier: T) {
        return dialog.getData<AppDialogData<T>>(identifier);
    }

    const openIdentifiers = dialog.openIdentifiers as AppDialog[];

    return {
        close: dialog.close,
        open,
        getData,
        isOpen: dialog.isOpen,
        openIdentifiers,
        setPreventClose: dialog.setPreventClose,
        preventClose: dialog.preventClose
    };
}
